import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { IconoAnadir, IconoBuscar, IconoQuitar } from '../../componentes/iconos'
import type { ProductoCatalogo } from './catalogo'
import { esBono, filtrarProductos, productosSueltos } from './catalogo'
import { formatearEuros } from './formato'

/**
 * Anadir productos sueltos al pedido, los que sean.
 *
 * Sustituye al desplegable de "extras", que se quedaba corto por dos motivos
 * medidos contra Odoo: dejaba fuera envases y devoluciones (y con ellos la
 * garrafa suelta, la forma mas comun de los 114 pedidos sin bono), y un `select`
 * con 112 opciones no se navega. Aqui se teclea y se elige, como el buscador de
 * clientes de la misma pantalla.
 *
 * La cantidad **admite negativos**: la devolucion de envases va asi, y sin ella
 * un pedido de garrafa suelta no cuadra con los que crea hoy el flujo.
 */

export interface ProductoElegido {
  productoId: number
  nombre: string
  cantidad: number
  precio: number
}

interface Props {
  catalogo: ProductoCatalogo[]
  elegidos: ProductoElegido[]
  onCambiar: (elegidos: ProductoElegido[]) => void
}

/** Con 112 productos, una lista entera tapa la pantalla; con dos letras basta. */
const MAXIMO_SUGERENCIAS = 8

const CLASE_CAMPO =
  'w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo'

/**
 * La cantidad de una linea, editable y con signo.
 *
 * Lleva texto propio por la misma razon que `CampoPrecio`, y aqui el sintoma
 * era peor: escribir `-4` daba `14`. El guion suelto no es un numero, asi que
 * se rechazaba, el campo controlado volvia a `1`, y el `4` que venia detras se
 * pegaba a ese uno. Mientras se escribe manda el texto; solo sale hacia fuera
 * lo que ya es una cantidad valida.
 *
 * Cero no lo es: una linea de cero unidades no es una linea.
 */
function CampoCantidad({
  valor,
  etiqueta,
  onCambiar,
}: {
  valor: number
  etiqueta: string
  onCambiar: (cantidad: number) => void
}) {
  const [texto, setTexto] = useState(String(valor))
  const ultimo = useRef(valor)

  useEffect(() => {
    if (valor === ultimo.current) return
    ultimo.current = valor
    setTexto(String(valor))
  }, [valor])

  return (
    <input
      type="number"
      step="1"
      aria-label={etiqueta}
      value={texto}
      onChange={(evento) => {
        const escrito = evento.target.value
        setTexto(escrito)
        const cantidad = Number(escrito)
        if (escrito.trim() === '' || Number.isNaN(cantidad) || cantidad === 0) return
        ultimo.current = cantidad
        onCambiar(cantidad)
      }}
      onBlur={() => {
        // Al salir, un campo a medias (vacio, `-`, `0`) vuelve a lo ultimo
        // valido: dejarlo asi ensenaria una cantidad que no es la que se
        // publicaria.
        setTexto(String(ultimo.current))
      }}
      className="w-20 rounded-lg border border-borde-fuerte bg-superficie px-2 py-1 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
    />
  )
}

export function SelectorProductos({ catalogo, elegidos, onCambiar }: Props) {
  const idBuscador = useId()
  const [consulta, setConsulta] = useState('')

  const disponibles = useMemo(() => productosSueltos(catalogo), [catalogo])
  const sugerencias = useMemo(
    () => filtrarProductos(disponibles, consulta).slice(0, MAXIMO_SUGERENCIAS),
    [disponibles, consulta],
  )

  const anadir = (producto: ProductoCatalogo) => {
    const yaEsta = elegidos.find((elegido) => elegido.productoId === producto.id)
    onCambiar(
      yaEsta
        ? // Pulsar dos veces el mismo producto es pedir dos, no un fallo: antes
          // se ignoraba en silencio y parecia que el boton no hacia nada.
          elegidos.map((elegido) =>
            elegido.productoId === producto.id
              ? { ...elegido, cantidad: elegido.cantidad + 1 }
              : elegido,
          )
        : [
            ...elegidos,
            {
              productoId: producto.id,
              nombre: producto.nombre,
              cantidad: 1,
              precio: producto.precio,
            },
          ],
    )
    setConsulta('')
  }

  const cambiarCantidad = (productoId: number, cantidad: number) => {
    onCambiar(
      elegidos.map((elegido) =>
        elegido.productoId === productoId ? { ...elegido, cantidad } : elegido,
      ),
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={idBuscador} className="block text-sm font-medium text-tinta-2">
        Productos del pedido
      </label>
      <p className="text-sm text-tinta-apagada">
        Garrafas sueltas, packs, palets, dispensador, fianza, portes, cafe. Tambien envases y su
        devolucion, que van en cantidad negativa.
      </p>
      <div className="relative max-w-lg">
        <IconoBuscar
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-apagada"
        />
        <input
          id={idBuscador}
          type="search"
          autoComplete="off"
          placeholder="Buscar producto: orotana, pack, dispensador"
          value={consulta}
          onChange={(evento) => setConsulta(evento.target.value)}
          className={`${CLASE_CAMPO} pl-9`}
        />
      </div>

      {consulta.trim() !== '' ? (
        sugerencias.length === 0 ? (
          <p className="text-sm text-tinta-apagada">
            Ningun producto del catalogo de Odoo casa con "{consulta}".
          </p>
        ) : (
          <ul aria-label="Productos del catalogo" className="flex max-w-lg flex-col gap-1">
            {sugerencias.map((producto) => (
              <li key={producto.id}>
                <button
                  type="button"
                  onClick={() => anadir(producto)}
                  className="flex w-full items-center gap-2 rounded-lg border border-borde bg-superficie px-3 py-2 text-left text-sm transition-colors hover:bg-elevado"
                >
                  <IconoAnadir aria-hidden="true" className="size-4 shrink-0 text-tinta-apagada" />
                  <span className="text-tinta">{producto.nombre}</span>
                  {esBono(producto.nombre) ? (
                    <span className="shrink-0 rounded-full bg-atencion-fondo px-2 py-0.5 text-xs font-semibold text-atencion-tinta ring-1 ring-atencion-anillo">
                      va solo
                    </span>
                  ) : null}
                  <span className="ml-auto shrink-0 text-tinta-apagada">
                    {formatearEuros(producto.precio)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}

      {elegidos.some((elegido) => esBono(elegido.nombre)) ? (
        <p
          role="status"
          className="rounded-lg border border-atencion-anillo bg-atencion-fondo px-3 py-2 text-sm text-atencion-tinta"
        >
          Has anadido un bono como linea suelta: entra <strong>solo</strong>, sin los envases ni la
          devolucion que arrastra el selector de bonos. Si hacen falta, anadelos tu (la devolucion,
          en cantidad negativa).
        </p>
      ) : null}

      {elegidos.length > 0 ? (
        <ul aria-label="Productos anadidos" className="flex flex-col gap-2">
          {elegidos.map((elegido) => (
            <li
              key={elegido.productoId}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-borde px-3 py-2"
            >
              <span className="text-sm text-tinta">{elegido.nombre}</span>
              <CampoCantidad
                valor={elegido.cantidad}
                etiqueta={`Unidades de ${elegido.nombre}`}
                onCambiar={(cantidad) => cambiarCantidad(elegido.productoId, cantidad)}
              />
              <span className="text-sm text-tinta-apagada">{formatearEuros(elegido.precio)}</span>
              <button
                type="button"
                aria-label={`Quitar ${elegido.nombre}`}
                onClick={() =>
                  onCambiar(elegidos.filter((otro) => otro.productoId !== elegido.productoId))
                }
                className="ml-auto rounded-lg p-1 text-tinta-apagada transition-colors hover:bg-elevado hover:text-tinta"
              >
                <IconoQuitar aria-hidden="true" className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
